'use strict'

/**
 * Merge reviewed CV draft into current form state (in-memory).
 * Spec: scripts/cv-esco-v2-apply-merge-design.md
 */

const normalizeText = (value) => {
  return String(value || '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
}

const parseYear = (value) => {
  const match = String(value || '').match(/\b(?:19|20)\d{2}\b/)
  return match ? Number(match[0]) : null
}

const cloneEducation = (row) => ({
  name: row.name,
  university: row.university,
  level: row.level,
  startAndEndYear: Array.isArray(row.startAndEndYear)
    ? row.startAndEndYear.slice()
    : row.startAndEndYear
})

const cloneJob = (row) => ({
  occupation: row.occupation,
  company: row.company,
  start: row.start,
  end: row.end
})

const cloneEscoEntry = (row) => {
  const entry = {
    uri: row.uri,
    preferredLabel: row.preferredLabel,
    conceptType: row.conceptType,
    relation: row.relation,
    evidenceId: row.evidenceId,
    evidence: row.evidence,
    source: row.source,
    extractorCategory: row.extractorCategory || (row.source && row.source.type)
  }
  if (typeof row.checked === 'boolean') entry.checked = row.checked
  return entry
}

/** Una fila por evidenceId (como en el diálogo de review), idempotente. */
const mergeEscoFlatEntries = (existing, incoming) => {
  const byEvidenceId = new Map()
  for (const row of existing || []) {
    if (row && row.evidenceId) byEvidenceId.set(row.evidenceId, cloneEscoEntry(row))
  }
  for (const row of incoming || []) {
    if (!row || !row.evidenceId) continue
    const next = cloneEscoEntry(row)
    const prev = byEvidenceId.get(row.evidenceId)
    if (prev && typeof prev.checked === 'boolean' && typeof next.checked !== 'boolean') {
      next.checked = prev.checked
    }
    byEvidenceId.set(row.evidenceId, next)
  }
  return Array.from(byEvidenceId.values())
}

const suggestionIdentityKey = (row) => {
  const uri = String((row && row.uri) || '').trim()
  if (uri) return 'uri\0' + uri
  return 'label\0' + normalizeText(row && row.preferredLabel)
}

const tagNameOf = (entry) => {
  if (typeof entry === 'string') return String(entry).trim()
  if (!entry || typeof entry !== 'object') return ''
  return String(entry.name || entry.preferredLabel || '').trim()
}

const tagUriOf = (entry) => {
  if (!entry || typeof entry !== 'object') return ''
  return String(entry.uri || '').trim()
}

/**
 * Primera aparición de un concepto: checked.
 * Desmarcado si el mismo URI/etiqueta ya salió en la lista, o si la etiqueta
 * ya está en el formulario (chip guardado o tag añadido a mano).
 * Un checked explícito (el usuario ya lo tocó) se conserva.
 */
const stampSuggestionChecks = (rows, existingLabels) => {
  const takenLabels = new Set(
    (existingLabels || []).map((label) => normalizeText(label)).filter(Boolean)
  )
  const seenIdentity = new Set()
  const seenLabels = new Set()
  return (rows || []).map((row) => {
    const label = normalizeText(row && row.preferredLabel)
    const identity = suggestionIdentityKey(row)
    if (row && typeof row.checked === 'boolean') {
      if (identity) seenIdentity.add(identity)
      if (label) seenLabels.add(label)
      return Object.assign({}, row)
    }
    const already =
      (label && (takenLabels.has(label) || seenLabels.has(label))) ||
      (identity && seenIdentity.has(identity))
    if (identity) seenIdentity.add(identity)
    if (label) seenLabels.add(label)
    return Object.assign({}, row, { checked: !already })
  })
}

const stampFlatSuggestionChecks = (flat, labelsByBucket, bucketOf) => {
  const groups = new Map()
  for (const row of flat || []) {
    const bucket = bucketOf(row)
    if (!groups.has(bucket)) groups.set(bucket, [])
    groups.get(bucket).push(row)
  }
  const stampedByBucket = new Map()
  for (const [bucket, rows] of groups) {
    const labels = labelsByBucket && labelsByBucket[bucket]
    stampedByBucket.set(bucket, stampSuggestionChecks(rows, labels))
  }
  const cursors = new Map()
  return (flat || []).map((row) => {
    const bucket = bucketOf(row)
    const index = cursors.get(bucket) || 0
    cursors.set(bucket, index + 1)
    return stampedByBucket.get(bucket)[index]
  })
}

/** Nombres ya guardados, tags manuales y sugerencias marcadas, sin duplicar etiqueta ni URI. */
const mergeProfileTags = (saved, manual, suggested) => {
  const seenLabels = new Set()
  const seenUris = new Set()
  const out = []
  const push = (entry) => {
    const name = tagNameOf(entry)
    const uri = tagUriOf(entry)
    const labelKey = normalizeText(name)
    if (!labelKey && !uri) return
    if (labelKey && seenLabels.has(labelKey)) return
    if (uri && seenUris.has(uri)) return
    if (labelKey) seenLabels.add(labelKey)
    if (uri) seenUris.add(uri)
    if (uri) out.push({ name, uri })
    else out.push(name)
  }
  for (const list of [saved, manual, suggested]) {
    for (const entry of list || []) push(entry)
  }
  return out
}

const cloneConcept = (row) => ({
  uri: row.uri,
  preferredLabel: row.preferredLabel,
  conceptType: row.conceptType,
  relation: row.relation,
  evidences: (row.evidences || []).map((ev) => ({
    evidenceId: ev.evidenceId,
    evidence: ev.evidence
  }))
})

const jobIdentityKey = (job) => {
  return [
    normalizeText(job.occupation),
    normalizeText(job.company),
    normalizeText(job.start),
    normalizeText(job.end)
  ].join('\0')
}

const jobLooseKey = (job) => {
  return [
    normalizeText(job.occupation),
    normalizeText(job.company)
  ].join('\0')
}

const educationIdentityKey = (name, university) => {
  return normalizeText(name) + '\0' + normalizeText(university)
}

const draftEducationToFormRow = (draftRow) => {
  const name = String((draftRow && draftRow.title) || (draftRow && draftRow.name) || '').trim()
  const university = String((draftRow && draftRow.university) || '').trim()
  const startYear = parseYear(draftRow && draftRow.start)
  const endYear = parseYear(draftRow && draftRow.end)
  let startAndEndYear = [1970, 1970]
  if (startYear && endYear) startAndEndYear = [startYear, endYear]
  else if (startYear) startAndEndYear = [startYear, startYear]
  else if (endYear) startAndEndYear = [endYear, endYear]
  return {
    name,
    university,
    level: String((draftRow && draftRow.level) || '').trim(),
    startAndEndYear,
    _draftStart: normalizeText(draftRow && draftRow.start),
    _draftEnd: normalizeText(draftRow && draftRow.end)
  }
}

const stripEducationMeta = (row) => {
  const out = cloneEducation(row)
  delete out._draftStart
  delete out._draftEnd
  return out
}

const endYearIsExplicit = (start, end) => {
  return end != null && (start == null || end !== start)
}

const yearsConflict = (existingRange, incomingRange) => {
  const [es, ee] = existingRange || []
  const [is, ie] = incomingRange || []
  if (es != null && is != null && es !== is) return true
  const exEnd = endYearIsExplicit(es, ee)
  const inEnd = endYearIsExplicit(is, ie)
  if (exEnd && inEnd && ee !== ie) return true
  return false
}

const mergeEducationFields = (existing, incoming) => {
  const out = cloneEducation(existing)
  if (!normalizeText(out.university) && normalizeText(incoming.university)) {
    out.university = incoming.university
  }
  if (!normalizeText(out.level) && normalizeText(incoming.level)) {
    out.level = incoming.level
  }
  if (!normalizeText(out.name) && normalizeText(incoming.name)) {
    out.name = incoming.name
  }
  const [es, ee] = out.startAndEndYear || [null, null]
  const [is, ie] = incoming.startAndEndYear || [null, null]
  if (yearsConflict(out.startAndEndYear, incoming.startAndEndYear)) {
    return out
  }
  if (!endYearIsExplicit(es, ee) && endYearIsExplicit(is, ie)) {
    out.startAndEndYear = [es != null ? es : is, ie]
  } else if ((es == null || es === 1970) && is != null) {
    out.startAndEndYear = incoming.startAndEndYear.slice()
  }
  return out
}

const mergeJobIntoList = (jobs, incoming) => {
  const next = jobs.map(cloneJob)
  const exactKey = jobIdentityKey(incoming)
  if (next.some((row) => jobIdentityKey(row) === exactKey)) {
    return next
  }
  const loose = jobLooseKey(incoming)
  const existing = next.find((row) => jobLooseKey(row) === loose)
  if (existing) {
    const incStart = normalizeText(incoming.start)
    const incEnd = normalizeText(incoming.end)
    const exStart = normalizeText(existing.start)
    const exEnd = normalizeText(existing.end)
    if (incStart && exStart && incStart !== exStart) return next
    if (incEnd && exEnd && incEnd !== exEnd) return next
    if (!exStart && incStart) existing.start = incoming.start
    if (!exEnd && incEnd) existing.end = incoming.end
    return next
  }
  next.push(cloneJob(incoming))
  return next
}

const mergeEducationIntoList = (educations, draftRow) => {
  const incoming = draftEducationToFormRow(draftRow)
  const key = educationIdentityKey(incoming.name, incoming.university)
  const next = educations.map(cloneEducation)
  const index = next.findIndex(
    (row) => educationIdentityKey(row.name, row.university) === key
  )
  if (index < 0) {
    next.push(stripEducationMeta(incoming))
    return next
  }
  next[index] = mergeEducationFields(next[index], incoming)
  return next
}

const clonePendingEducation = (row) => {
  const out = {
    name: row.name,
    university: row.university,
    level: row.level,
    startAndEndYear: Array.isArray(row.startAndEndYear)
      ? row.startAndEndYear.slice()
      : row.startAndEndYear
  }
  if (typeof row.checked === 'boolean') out.checked = row.checked
  return out
}

/** Sugerencias de educación, una fila por nombre+centro. El check del usuario se conserva. */
const mergePendingEducations = (existing, incomingRows) => {
  const next = (existing || []).map(clonePendingEducation)
  for (const draftRow of incomingRows || []) {
    const incoming = stripEducationMeta(draftEducationToFormRow(draftRow))
    const key = educationIdentityKey(incoming.name, incoming.university)
    const index = next.findIndex(
      (row) => educationIdentityKey(row.name, row.university) === key
    )
    if (index < 0) {
      next.push(incoming)
      continue
    }
    const prevChecked = next[index].checked
    const merged = mergeEducationFields(next[index], incoming)
    if (typeof prevChecked === 'boolean') merged.checked = prevChecked
    next[index] = merged
  }
  return next
}

/**
 * Primera aparición: marcada.
 * Desmarcada si el mismo nombre+centro ya está guardado o ya salió en la lista.
 * Un check explícito se conserva.
 */
const stampEducationChecks = (rows, existingEducations) => {
  const taken = new Set(
    (existingEducations || [])
      .map((row) => educationIdentityKey(row && row.name, row && row.university))
      .filter((key) => key && key !== '\0')
  )
  const seen = new Set()
  return (rows || []).map((row) => {
    const key = educationIdentityKey(row && row.name, row && row.university)
    if (row && typeof row.checked === 'boolean') {
      if (key && key !== '\0') seen.add(key)
      return Object.assign({}, row)
    }
    const already = key && key !== '\0' && (taken.has(key) || seen.has(key))
    if (key && key !== '\0') seen.add(key)
    return Object.assign({}, row, { checked: !already })
  })
}

/** Formulario guardado más las sugerencias marcadas. No duplica nombre+centro. */
const educationsForSave = (saved, pending) => {
  let next = (saved || []).map(cloneEducation)
  for (const row of pending || []) {
    if (!row || row.checked !== true) continue
    next = mergeEducationIntoList(next, {
      title: row.name,
      university: row.university,
      level: row.level,
      start: row.startAndEndYear && row.startAndEndYear[0],
      end: row.startAndEndYear && row.startAndEndYear[1]
    })
  }
  return next
}

const mergeEscoConcepts = (concepts, incomingRows) => {
  const byUri = new Map()
  for (const row of concepts || []) {
    if (!row || !row.uri) continue
    byUri.set(row.uri, cloneConcept(row))
  }
  for (const row of incomingRows || []) {
    if (!row || !row.uri) continue
    const evidenceEntry = {
      evidenceId: row.evidenceId,
      evidence: row.evidence
    }
    const prev = byUri.get(row.uri)
    if (!prev) {
      byUri.set(row.uri, {
        uri: row.uri,
        preferredLabel: row.preferredLabel,
        conceptType: row.conceptType,
        relation: row.relation,
        evidences: [evidenceEntry]
      })
      continue
    }
    if (prev.conceptType == null && row.conceptType != null) {
      prev.conceptType = row.conceptType
    }
    if (!prev.evidences.some((ev) => ev.evidenceId === row.evidenceId)) {
      prev.evidences.push(evidenceEntry)
    }
  }
  return Array.from(byUri.values())
}

const applyReviewedDraftToForm = (formState, reviewedDraft) => {
  const state = formState || {}
  const draft = reviewedDraft || {}
  let cvEscoV2AppliedJobs = (state.cvEscoV2AppliedJobs || []).map(cloneJob)
  const educationsSelected = (state.educationsSelected || []).map(cloneEducation)
  let cvEscoV2AppliedConcepts = (state.cvEscoV2AppliedConcepts || []).map(cloneConcept)
  const cvEscoV2AppliedEducations = stampEducationChecks(
    mergePendingEducations(state.cvEscoV2AppliedEducations, draft.education || []),
    educationsSelected
  )

  // draft.jobs (texto extractor) ignorado; job:* ESCO va en cvEscoV2AppliedConcepts → Job Experiences UI.
  // La educación no entra en el formulario hasta que el usuario marca el check y pulsa Update.
  cvEscoV2AppliedConcepts = mergeEscoConcepts(cvEscoV2AppliedConcepts, draft.esco || [])

  return {
    educationsSelected,
    cvEscoV2AppliedEducations,
    cvEscoV2AppliedJobs,
    cvEscoV2AppliedConcepts,
    experiencesSelected: state.experiencesSelected ? state.experiencesSelected.slice() : [],
    skillsSelected: state.skillsSelected ? state.skillsSelected.slice() : [],
    competencesSelected: state.competencesSelected ? state.competencesSelected.slice() : []
  }
}

const formStatesEqual = (a, b) => {
  return JSON.stringify(a) === JSON.stringify(b)
}

module.exports = {
  normalizeText,
  parseYear,
  jobIdentityKey,
  educationIdentityKey,
  draftEducationToFormRow,
  mergeJobIntoList,
  mergeEducationIntoList,
  mergePendingEducations,
  stampEducationChecks,
  educationsForSave,
  mergeEscoFlatEntries,
  mergeEscoConcepts,
  stampSuggestionChecks,
  stampFlatSuggestionChecks,
  mergeProfileTags,
  applyReviewedDraftToForm,
  formStatesEqual
}
